<template>
  <v-dialog
      fullscreen
      eager
      v-model="dialog"
      hide-overlay
      content-class="form-dialog"
  >
    <v-card elevation="0">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog" ><v-icon large color="primary">close</v-icon></v-btn>
      </v-col>

      <v-card-text>
        <v-form ref="form">
          <v-row class="pa-5" justify="center">
            <v-col cols="12" md="11" lg="9">
              <h3 class="font-weight-bold">
                {{ edit ? $t("settings/profile:customer.user-management.title-edit") : $t("buttons:add-specific", { specific: $t('common:user')}) }}
              </h3>
            </v-col>
            <v-col cols="12" md="5" lg="4">

              <!-- Account Section -->
              <h4 class="primary--text font-weight-medium">{{ $t('settings/profile:customer.user-management.account') }}</h4>
              <!-- Email -->

              <DuplicateCheckField
                  ref="duplEmail"
                  :value="data.email"
                  :label="$t('common:email')"
                  item-additional-info-field="email"
                  required
                  tab-index="1"
                  item-text="name"
                  @input="data.email = $event"
                  @checkForDuplicates="checkByEmail"
                  @clickedShowItem="showUser"
              />

              <!-- Password -->
              <v-text-field
                  outlined
                  v-model="data.password"
                  :append-icon="passwordShow ? 'visibility_off' : 'visibility'"
                  :rules="[ruleEmptyOrMinLength(8,$t('utils:rules.min8Characters'))]"
                  :type="passwordShow ? 'text' : 'password'"
                  :label="$t('settings/profile:common.password')"
                  counter
                  @click:append="passwordShow = !passwordShow"
                  tabindex="2"
              />

              <!-- Book Role -->
              <v-select
                  :items="roleList"
                  item-value="value"
                  item-text="text"
                  v-model="bookHierarchy"
                  :menu-props="{ bottom: true, offsetY: true }"
                  outlined
                  :clearable="isAdmin"
                  :label="$t('settings/profile:customer.user-management.labels.hierarchy')"
                  :rules="isBookRoleRequire"
                  tabindex="3"
              />

              <!-- Display Role -->
              <v-autocomplete
                  v-if="isAdmin"
                  clearable
                  outlined
                  hide-selected
                  multiple
                  small-chips
                  deletable-chips
                  :menu-props="{bottom: true, offsetY: true}"
                  :items="displayRoleList"
                  item-text="text"
                  item-value="value"
                  :value="data.displayRoles"
                  @input="e => data.displayRoles = e"
                  :label="$t('settings/profile:customer.user-management.labels.display-hierarchy')"
              />

              <!-- Profile Section -->
              <h4 class="primary--text font-weight-medium mt-5">{{ $t('settings/profile:common.profile') }}</h4>
              <!-- Name -->
              <DuplicateCheckField
                  ref="duplName"
                  :value="data.name"
                  :label="$t('common:name')"
                  item-additional-info-field="email"
                  required
                  tab-index="4"
                  item-text="name"
                  @input="data.name = $event"
                  @checkForDuplicates="checkByName"
                  @clickedShowItem="showUser"
              />

              <!-- Phone -->
              <v-text-field
                  outlined
                  v-model="data.phone"
                  :label="$t('settings/profile:common.phone')"
                  tabindex="5"
              />

              <CustomerTypeahead
                  v-show="isAdmin"
                  ref="customerTypeahead"
                  :value="data.customer"
                  @input="data.customer = $event"
                  :label="$t('common:customer')"
                  tabindex="6"
              />

              <div v-if="$vuetify.breakpoint.mdAndUp">
                <BaseButton
                    color="primary"
                    :btn-text="$t('buttons:save')"
                    @clickedButton="saveUser()"
                />
                <TwoButtonDialog
                    v-if="edit"
                    ref="deleteButton"
                    :btn-text="$t('buttons:delete')"
                    color="error"
                    :dialog-title="$t('dialog:delete-btn-title', {name : $t('settings/profile:customer.user-management.dialog-part-1')})"
                    :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('settings/profile:customer.user-management.dialog-part-2')})"
                    :button1-text="$t('buttons:cancel')"
                    :button2-text="$t('buttons:delete')"
                    @clickedButton1="$refs.deleteButton.showDialog = false"
                    @clickedButton2="deleteUser()"
                />
                <DirtyConfirmButtonDialog
                    ref="dirtyDialog"
                    :btn-text="$t('buttons:cancel')"
                    color="grey"
                    :dialog-title="$t('dialog:dirty-save-btn-title')"
                    :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                    :button1-text="$t('dialog:dirty-save-btn-1-text')"
                    :button2-text="$t('dialog:dirty-save-btn-2-text')"
                    @clickedButton="closeDialog()"
                    :dataToWatch="data"
                    hideOverlay
                />
              </div>
            </v-col>

            <v-col class="d-none d-md-block" md="1"></v-col>

            <v-col cols="12" md="5" lg="4">
              <!-- Settings Section -->
              <h4 class="primary--text font-weight-medium">{{ $t('common:settings') }}</h4>
              <!-- Language -->
              <v-select
                  :items="languages"
                  v-model="data.language"
                  outlined
                  :menu-props="{ bottom: true, offsetY: true }"
                  :label="$t('settings/profile:common.language')"
                  tabindex="7"
              />

              <!-- TODO certificate datatable and buttons -->

              <!-- Active -->
              <CheckboxTooltipHint
                  ref="activeCheckbox"
                  color="secondary"
                  :label="$t('common:active')"
                  :tooltip-line1="$t('settings/profile:customer.user-management.tooltips.active-1')"
                  :tooltip-line2="$t('settings/profile:customer.user-management.tooltips.active-2')"
                  @updateCheckbox="data.active = $event"
                  tab-index="8"
              />

              <!-- Main User -->
              <CheckboxTooltipHint
                  ref="mainCheckbox"
                  color="secondary"
                  :label="$t('settings/profile:customer.user-management.labels.main')"
                  :tooltip-line1="$t('settings/profile:customer.user-management.tooltips.main')"
                  @updateCheckbox="data.main = $event"
                  tab-index="9"
              />

              <!-- Mail Notification -->
              <CheckboxTooltipHint
                  ref="mailNotificationCheckbox"
                  color="secondary"
                  :label="$t('settings/profile:common.mail-notifications')"
                  :tooltip-line1="$t('settings/profile:customer.user-management.tooltips.mail-notification')"
                  @updateCheckbox="data.mailNotification = $event"
                  tab-index="10"
              />

              <!-- Mail Confirmation -->
              <v-checkbox
                  v-if="edit"
                  color="secondary"
                  v-model="data.mailConfirmation"
                  :label="$t('settings/profile:customer.user-management.labels.mail-confirmation')"
                  disabled
                  tabindex="11"
              />

              <!-- Hide Warning -->
              <CheckboxTooltipHint
                  ref="hideWarningCheckbox"
                  color="secondary"
                  :label="$t('settings/profile:customer.user-management.labels.hide-warning')"
                  :tooltip-line1="$t('settings/profile:customer.user-management.tooltips.hide-warning')"
                  @updateCheckbox="data.hideWarning = $event"
                  tab-index="12"
              />

              <div v-if="$vuetify.breakpoint.smAndDown">
                <BaseButton
                    color="primary"
                    :btn-text="$t('buttons:save')"
                    @clickedButton="saveUser()"
                />
                <TwoButtonDialog
                    v-if="edit"
                    ref="deleteButton"
                    :btn-text="$t('buttons:delete')"
                    color="error"
                    :dialog-title="$t('dialog:delete-btn-title', {name : $t('settings/profile:customer.user-management.dialog-part-1')})"
                    :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('settings/profile:customer.user-management.dialog-part-2')})"
                    :button1-text="$t('buttons:cancel')"
                    :button2-text="$t('buttons:delete')"
                    @clickedButton1="$refs.deleteButton.showDialog = false"
                    @clickedButton2="deleteUser()"
                />
                <DirtyConfirmButtonDialog
                    ref="dirtyDialog"
                    :btn-text="$t('buttons:cancel')"
                    color="grey"
                    :dialog-title="$t('dialog:dirty-save-btn-title')"
                    :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                    :button1-text="$t('dialog:dirty-save-btn-1-text')"
                    :button2-text="$t('dialog:dirty-save-btn-2-text')"
                    @clickedButton="closeDialog()"
                    :dataToWatch="data"
                    hideOverlay
                />
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import UsersService from '@/modules/core/services/users-service';
import CustomerService from "@/modules/core/services/customer-service";
import RoleAuthService from "@/modules/core/services/auth/role-auth-service";

import Utils from "@/modules/core/services/utils";
import DataUtils from "@/modules/core/services/data-utils";
import {ruleEmptyOrMinLength, ruleRequired} from "@/modules/core/services/rules";

import Users from "@/modules/core/data/users";
import Customer from "@/modules/core/data/customer"

import CustomerTypeahead from "@/modules/core/components/CustomerTypeAhead";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";

import {mapGetters} from 'vuex';
import CheckboxTooltipHint from "@/modules/core/components/CheckboxTooltipHint";
import DuplicateCheckField from "@/modules/core/components/DuplicateCheckField";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";

export default {
  name: 'UsersDialogForm',
  components: {
    DuplicateCheckField,
    CheckboxTooltipHint,
    TwoButtonDialog,
    CustomerTypeahead,
    BaseButton,
    DirtyConfirmButtonDialog
  },
  data() {
    return {
      data: new Users(),
      edit: false,
      passwordShow: false,
      roles: [],
      displayRoles: [],
      rules: {
        emptyOrMin: v => !v || v.length == 0 || v.length >= 8 || this.$t('utils:rules.min8Characters')
      },
      isFetchingUser: true,
      bookHierarchy: '',

      dialog: false,
      tableOptions: [],
      tableSearch: []
    }
  },
  computed: {
    languages() {
      return [
        {value: "GERMAN", text: this.$t('enums:languages.german')},
        {value: "ENGLISH", text: this.$t('enums:languages.english')},
      ]
    },
    ...mapGetters('auth', ['isAdmin']),
    currentLang() {
      return this.$store.state.translation.language;
    },
    showComponent() {
      if(this.edit) {
        if(this.isFetchingUser) {
          return false
        } else {
          return true;
        }
      } else {
        return true
      }
    },
    isBookRoleRequire() {
      if (this.isAdmin && this.data.displayRoles && this.data.displayRoles.length > 0) return [];

      return [ruleRequired(this.$t('utils:rules.required'))];
    },
    roleList() {
      let roles = this.roles;
      for(let i = 0; i < roles.length; i++) {
        roles[i].text = this.$t('enums:hierarchy.' + roles[i].value);
      }
      return roles;
    },
    displayRoleList() {
      let roles = this.displayRoles;
      for(let i = 0; i < roles.length; i++) {
        roles[i].text = this.$t('enums:hierarchy.' + roles[i].value);
      }
      return roles;
    }
  },
  methods: {
    closeDialog() {
      this.dialog = false;
      this.data = new Users();

      this.$refs.form.reset();
    },

    addUser(options, search) {
      this.tableOptions = options;
      this.tableSearch = search;

      this.edit = false;
      this.dialog = true;

      //load and set the default customer data to the customer fields
      CustomerService.readProfileFromAPI(
        (res) => {
          var response = DataUtils.parseDataToSingleObject(res.data, Customer)
          if (response) {
            this.data.customer = response.id;
            this.$refs.customerTypeahead.initialise(this.data.customer);
            this.$refs.dirtyDialog.startWatch();
          }
        },
        (error) => {
          Utils.showWarningSnackbar(this.$t('settings/profile:customer.user-management.snackbar.customer-load-failure'))
          this.$logger.error(error);
        }
      )
    },

    editUser(id, options, search) {
      this.tableOptions = options;
      this.tableSearch = search;

      this.edit = true;
      this.dialog = true;

      UsersService.readFromAPI(id,
        (resp) => {
          var response = DataUtils.parseDataToSingleObject(resp.data, Users);
          if (response == null) {
            this.$router.go(-1);
          } else {
            this.data = response;
            this.bookHierarchy = response.bookHierarchy;
            this.$refs.customerTypeahead.initialise(this.data.customer);
            this.$refs.activeCheckbox.checkboxValue = this.data.active;
            this.$refs.mainCheckbox.checkboxValue = this.data.main;
            this.$refs.mailNotificationCheckbox.checkboxValue = this.data.mailNotification;
            this.$refs.hideWarningCheckbox.checkboxValue = this.data.hideWarning;

            this.isFetchingUser = false;
            this.$refs.dirtyDialog.startWatch();
          }
        },
        (error) => {
          this.$logger.error(error);
          this.$router.push('/404')
        }
      )
    },

    saveUser() {
      //TODO mail check
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      if (!this.bookHierarchy) {
        this.$refs.mainCheckbox.setValue(false);
      }

      if (this.edit) {
        UsersService.putToAPI(this.data,
            () => {
              Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('common:user')}));

              this.closeDialog();
              this.$emit('updateUsers', this.tableOptions, this.tableSearch);
            },
            (serviceName, error) => {
              if (error.response.headers.error_cause && this.$t('error-causes:' + error.response.headers.error_cause)) {
                Utils.showFailureSnackbar(this.$t("error-causes:" + error.response.headers.error_cause));
              } else {
                Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('common:user')}))
              }
            }
        )
      } else {
        let userRoleName = this.roles.find(role => role.value === this.data.bookHierarchy);

        UsersService.registerUser(this.data, userRoleName ? userRoleName.text : null,
            () => {
              if (this.data.active) {
                Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('common:user')}));
              } else {
                Utils.showSuccessSnackbar(this.$t("components:minimized-users-form.snackbar.register-success"));
              }
              this.closeDialog();
              this.$emit('updateUsers', this.tableOptions, this.tableSearch)
            },
            (serviceName, error) => {
              if (error.response.headers.error_cause && this.$t('error-causes:' + error.response.headers.error_cause)) {
                Utils.showFailureSnackbar(this.$t("error-causes:" + error.response.headers.error_cause));
              } else {
                Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:user')}))
              }
            }
        )
      }
    },

    deleteUser() {
      this.$refs.deleteButton.setButtonSettings(true, true, false, true);

      UsersService.deleteFromAPI(this.data,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:user')}));

          this.closeDialog();
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$emit('updateUsers', this.tableOptions, this.tableSearch);
        },
        () => {
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:user')}))
        }
      )
    },

    checkByEmail(term) {
      DuplicateCheckService.checkUsers('EMAIL', term,
          (res) => {
            this.$refs.duplEmail.fillData(res.data);
          },
          (serviceName, error) => {
            this.handleDuplicateErrorCause(serviceName, error);
          }
      )
    },

    checkByName(term) {
      DuplicateCheckService.checkUsers('NAME', term,
          (res) => {
            this.$refs.duplName.fillData(res.data);
          },
          (serviceName, error) => {
            this.handleDuplicateErrorCause(serviceName, error);
          }
      )
    },

    showUser(item) {
      this.editUser(item.id, this.tableOptions, this.tableSearch);
    },

    handleDuplicateErrorCause(serviceName, error) {
      if (error.response.headers.error_cause && this.$t('error-causes:' + error.response.headers.error_cause)) {
        Utils.showWarningSnackbar(this.$t("error-causes:" + error.response.headers.error_cause));
      } else {
        this.$logger.error(serviceName + error);
      }
    },
    loadAuthorityEnums() {
      RoleAuthService.readAuthorities(
          (resp) => {
            resp.data.forEach((object) => {
              let role = new Object();
              role.value = object;
              role.text = this.$t('enums:hierarchy.' + object);
              this.roles.push(role);
            });
          },
          (error) => {
            this.$logger.info(error);
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/profile:customer.user-management.snackbar.user-role')}))
          }
      );

      if (this.isAdmin) {
        RoleAuthService.readDisplayAuthorities(
            (resp) => {
              resp.data.forEach((object) => {
                let role = new Object();
                role.value = object;
                role.text = this.$t('enums:hierarchy.' + object);
                this.displayRoles.push(role);
              });
            },
            (error) => {
              this.$logger.info(error);
              Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/profile:customer.user-management.snackbar.user-role')}))
            }
        );
      }
    },

    ruleEmptyOrMinLength,
    ruleRequired
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() =>{
        this.$refs.form.resetValidation();
      })
    },

    dialog() {
      if(this.dialog) {
        // check if url includes openForm=true and remove it so it doesn't reopen when the user refreshes the page.
        if (this.$route.query.user) {
          this.$router.replace(`/${this.$store.state.manager.settingsModuleUrl}/profile/customer/users`);
        }
      }
    },
    bookHierarchy() {
      this.data.bookHierarchy = this.bookHierarchy;

      if (!this.bookHierarchy) {
        this.$refs.mainCheckbox.setValue(false);
      }
    }
  },
  mounted() {
    this.loadAuthorityEnums();
  }
}
</script>
<style lang="scss">
.v-dialog__content.v-dialog__content--active {
  z-index: 500 !important;
}
</style>
